import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, FileText, History, LineChart, ShieldCheck, Mic, BookOpen, MessageSquare, CalendarClock, ArrowRight, TrendingDown, TrendingUp, Minus } from "lucide-react";
import api from "../../api";

// Overview tab on the patient file: the short version before a session
// (backend clinical/record/patient_overview.py). Clinical viewers only; the page shows
// counts and the care team to everyone else.
const TONE_DOT = { alert: "bg-rose-500", warn: "bg-amber-500", good: "bg-emerald-500", info: "bg-secondary-300" };
const BAND_STYLE = { ok: "bg-emerald-100 text-emerald-800", mild: "bg-lime-100 text-lime-800", moderate: "bg-amber-100 text-amber-800", severe: "bg-rose-100 text-rose-700" };
const SHOWN_CHANGES = 6;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const ago = (iso) => {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return fmtDate(iso);
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function Card({ icon: Icon, title, action, children, className = "" }) {
  return (
    <div className={`rounded-xl bg-white border border-secondary-100 p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary-600" />
        <p className="text-sm font-bold text-secondary-900">{title}</p>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </div>
  );
}

function Sparkline({ series, max }) {
  if (!series || series.length < 2) return null;
  const w = 72, h = 24;
  const top = Math.max(max || 0, ...series) || 1;
  const pts = series.map((v, i) => `${(i / (series.length - 1)) * w},${h - (v / top) * (h - 2) - 1}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="text-primary-500" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Trend({ m }) {
  if (m.previous == null || m.previous === m.score) return <Minus className="w-3.5 h-3.5 text-secondary-400" aria-label="No change" />;
  const better = (m.score > m.previous) === m.higher_is_better;
  const Icon = m.score > m.previous ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${better ? "text-emerald-700" : "text-rose-700"}`}>
      <Icon className="w-3.5 h-3.5" /> {m.previous} → {m.score}
    </span>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="text-secondary-600">{label}</span>
      <span className={`font-semibold text-right ${tone === "warn" ? "text-amber-700" : tone === "alert" ? "text-rose-700" : tone === "good" ? "text-emerald-700" : "text-secondary-800"}`}>{value}</span>
    </div>
  );
}

export default function PatientOverviewPanel({ patientId, record, onOpenTab }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [allChanges, setAllChanges] = useState(false);

  useEffect(() => {
    setData(null);
    api.getPatientOverview(patientId).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.error || "Couldn't load the overview."));
  }, [patientId]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <p className="text-sm text-secondary-500">Loading…</p>;

  const { why_here: why, last_session: last, changes, measures, snapshot } = data;
  const shownChanges = allChanges ? changes : changes.slice(0, SHOWN_CHANGES);
  const openChange = (c) => (c.link ? navigate(c.link) : c.tab && onOpenTab(c.tab));
  const risk = record.current_risk;
  const consent = snapshot.transcription_consent;

  return (
    <div className="grid gap-4 lg:grid-cols-3 items-start">
      <div className="lg:col-span-2 space-y-4">
        <Card icon={Compass} title="Why they're here">
          {why.from_plan || why.concerns ? (
            <div className="space-y-1.5 text-sm text-secondary-800">
              <p className="whitespace-pre-line">{why.from_plan || why.concerns}</p>
              {why.from_plan && why.concerns && why.concerns !== why.from_plan && (
                <p className="text-xs text-secondary-500">At first contact: {why.concerns}{why.duration ? ` (for ${why.duration})` : ""}</p>
              )}
              {!why.from_plan && why.duration && <p className="text-xs text-secondary-500">For {why.duration}</p>}
            </div>
          ) : (
            <p className="text-sm text-secondary-400">Not recorded yet. Add presenting concerns on the Profile tab, or presenting problems on the treatment plan.</p>
          )}
        </Card>

        <Card icon={FileText} title={last?.mine === false ? "Latest session" : "Your last session"}
          action={last && <button type="button" onClick={() => navigate(`/consultation/${last.consultation_id}`)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">Open notes <ArrowRight className="w-3.5 h-3.5" /></button>}>
          {!last ? <p className="text-sm text-secondary-400">No sessions yet.</p> : (
            <div className="space-y-3 text-sm">
              <p className="text-secondary-600">
                Session {last.session_number} · {fmtDate(last.date || last.at)}{last.clinician ? ` · ${last.clinician}` : ""}
                {last.note_status === "signed" || last.note_status === "final" ? " · signed" : last.status !== "completed" ? " · notes not finished" : ""}
              </p>
              <div className="rounded-lg bg-primary-50/60 border border-primary-100 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-700 mb-1">Focus for this session</p>
                <p className={`whitespace-pre-line ${last.focus_next ? "text-secondary-800" : "text-secondary-400"}`}>{last.focus_next || "No plan for the next session was written."}</p>
              </div>
              {last.themes && <p className="text-secondary-700"><span className="font-semibold">Themes:</span> {last.themes}</p>}
              {last.told_patient && (
                <details className="text-secondary-700">
                  <summary className="cursor-pointer text-xs font-semibold text-secondary-500">What the patient was given</summary>
                  <p className="mt-1.5 whitespace-pre-line">{last.told_patient}</p>
                </details>
              )}
              <p className="text-xs text-secondary-400">A summary of the session and of the case so far will appear here once the session summary is built.</p>
            </div>
          )}
        </Card>

        <Card icon={History} title={`Since ${data.since_label}`}
          action={<span className="text-xs text-secondary-400">{last ? fmtDate(data.since) : ""}</span>}>
          {changes.length === 0 ? (
            <p className="text-sm text-secondary-500">Nothing new since {data.since_label}: no questionnaires, check-ins, homework, messages or changes.</p>
          ) : (
            <>
              <ul className="divide-y divide-secondary-100">
                {shownChanges.map((c, i) => (
                  <li key={i}>
                    <button type="button" onClick={() => openChange(c)} className="w-full flex items-start gap-3 py-2 text-left hover:bg-secondary-50 rounded-md px-1">
                      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${TONE_DOT[c.tone] || TONE_DOT.info}`} aria-hidden="true" />
                      <span className="flex-1 min-w-0 text-sm text-secondary-800 break-words">{c.text}</span>
                      <span className="shrink-0 text-xs text-secondary-400 whitespace-nowrap">{ago(c.at)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {changes.length > SHOWN_CHANGES && (
                <button type="button" onClick={() => setAllChanges((v) => !v)} className="mt-2 text-xs font-semibold text-primary-600 hover:underline">
                  {allChanges ? "Show fewer" : `Show all ${changes.length}`}
                </button>
              )}
            </>
          )}
        </Card>
      </div>

      <div className="space-y-4">
        <Card icon={ShieldCheck} title="Safety & admin">
          <div className="divide-y divide-secondary-100">
            {record.sections.includes("risk") && (
              <Row label="Risk" value={risk ? `${cap(risk.level)} · ${ago(risk.assessed_at)}` : "Not assessed"}
                tone={risk?.level === "high" ? "alert" : risk?.level === "moderate" ? "warn" : risk ? "good" : undefined} />
            )}
            {snapshot.safety_plan && (
              <Row label="Safety plan" value={snapshot.safety_plan.exists ? (snapshot.safety_plan.shared ? "In place, shared" : "In place, not shared") : "None"}
                tone={!snapshot.safety_plan.exists && ["moderate", "high"].includes(risk?.level) ? "alert" : undefined} />
            )}
            <Row label={<span className="inline-flex items-center gap-1"><Mic className="w-3.5 h-3.5" /> Transcription</span>}
              value={consent ? (consent.decision === "granted" ? "Agreed" : "Declined") : "Not asked yet"}
              tone={consent?.decision === "declined" ? "warn" : undefined} />
            {snapshot.open_homework != null && (
              <Row label={<span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> Open homework</span>} value={snapshot.open_homework} />
            )}
            <Row label={<span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Unread messages</span>}
              value={snapshot.unread_messages} tone={snapshot.unread_messages ? "warn" : undefined} />
            <Row label={<span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Next visit</span>}
              value={record.next_appointment ? `${fmtDate(record.next_appointment.date)}, ${record.next_appointment.time}` : "None booked"}
              tone={record.next_appointment ? undefined : "warn"} />
          </div>
        </Card>

        {measures.length > 0 && (
          <Card icon={LineChart} title="Questionnaires"
            action={<button type="button" onClick={() => onOpenTab("measures")} className="text-xs font-semibold text-primary-600 hover:underline">All results</button>}>
            <ul className="space-y-3">
              {measures.map((m) => (
                <li key={m.code} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-secondary-900">
                      {m.name} <span className="font-bold">{m.score}</span>{m.max_score ? <span className="text-xs font-normal text-secondary-400">/{m.max_score}</span> : null}
                    </p>
                    <p className="flex flex-wrap items-center gap-1.5 mt-0.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BAND_STYLE[m.tone] || "bg-secondary-100 text-secondary-700"}`}>{cap(m.band)}</span>
                      <Trend m={m} />
                      <span className="text-[11px] text-secondary-400">{ago(m.at)}</span>
                    </p>
                  </div>
                  <Sparkline series={m.series} max={m.max_score} />
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
